import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { DocsNav } from '@/components/docs/DocsNav';
import { DocsSidebarNav, SidebarSection } from '@/components/docs/DocsSidebarNav';
import { MarkdownRenderer } from '@/components/shared/MarkdownRenderer';
import { useGuide, useGuides } from '@/hooks/useDocumentation';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Book, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function GuideArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { data: guide, isLoading, error } = useGuide(slug || '');
  const { data: allGuides } = useGuides();
  const { trackDocView } = useAnalytics();

  // Track guide view
  useEffect(() => {
    if (guide && slug) {
      trackDocView('guide', slug);
    }
  }, [guide, slug, trackDocView]);
  
  // Build sidebar sections
  const sidebarSections: SidebarSection[] = useMemo(() => {
    if (!allGuides) return [];
    
    return allGuides.categories.map((category) => ({
      title: category.name,
      slug: category.slug,
      items: allGuides.docs
        .filter((doc) => doc.category_id === category.id)
        .map((doc) => ({
          title: doc.title,
          slug: doc.slug,
          href: `/docs/guides/${doc.slug}`,
        })),
    }));
  }, [allGuides]);
  
  // Find prev/next articles
  const { prevArticle, nextArticle } = useMemo(() => {
    if (!allGuides || !slug) return { prevArticle: null, nextArticle: null };
    
    const allDocs = allGuides.docs;
    const currentIndex = allDocs.findIndex((d) => d.slug === slug);
    
    return {
      prevArticle: currentIndex > 0 ? allDocs[currentIndex - 1] : null,
      nextArticle: currentIndex < allDocs.length - 1 ? allDocs[currentIndex + 1] : null,
    };
  }, [allGuides, slug]);
  
  return (
    <Layout>
      <SEOHead 
        path={`/docs/guides/${slug}`}
        title={guide ? `${guide.title} | Guides | AutomotiveAPI Docs` : undefined}
        description={guide?.excerpt || undefined}
      />
      
      <DocsNav />
      
      <div className="container py-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <DocsSidebarNav sections={sidebarSections} className="hidden lg:block" />
          
          {/* Main Content */}
          <main className="flex-1 min-w-0 max-w-3xl">
            {/* Breadcrumb */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/docs">Docs</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/docs/guides">Guides</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                {guide && (
                  <>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                      <BreadcrumbPage>{guide.title}</BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
            
            {/* Loading State */}
            {isLoading && (
              <div className="space-y-4">
                <Skeleton className="h-10 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <div className="mt-8 space-y-3">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            )}
            
            {/* Error State */}
            {error && (
              <div className="text-center py-12">
                <Book className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Guide not found</h2>
                <p className="text-muted-foreground mb-4">
                  The guide you're looking for doesn't exist or has been moved.
                </p>
                <Button asChild variant="outline">
                  <Link to="/docs/guides">Browse all guides</Link>
                </Button>
              </div>
            )}
            
            {/* Article Content */}
            {guide && (
              <>
                <header className="mb-8">
                  <h1 className="text-3xl font-bold mb-2">{guide.title}</h1>
                  {guide.excerpt && (
                    <p className="text-lg text-muted-foreground">
                      {guide.excerpt}
                    </p>
                  )}
                </header>
                
                <article className="prose prose-slate dark:prose-invert max-w-none">
                  <MarkdownRenderer 
                    content={guide.content} 
                    generateHeadingIds 
                  />
                </article>
                
                {/* Prev/Next Navigation */}
                <nav className="mt-12 pt-8 border-t flex items-center justify-between">
                  {prevArticle ? (
                    <Link 
                      to={`/docs/guides/${prevArticle.slug}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <div className="text-right">
                        <div className="text-muted-foreground text-xs">Previous</div>
                        <div className="font-medium">{prevArticle.title}</div>
                      </div>
                    </Link>
                  ) : (
                    <div />
                  )}
                  
                  {nextArticle && (
                    <Link 
                      to={`/docs/guides/${nextArticle.slug}`}
                      className="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                    >
                      <div className="text-left">
                        <div className="text-muted-foreground text-xs">Next</div>
                        <div className="font-medium">{nextArticle.title}</div>
                      </div>
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  )}
                </nav>
              </>
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
}
